/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import { fromLonLat } from 'ol/proj';
import { colors } from '@mui/material';
import reducerPath from '../../../../app/reducerPath';
import { logisticTasksApi } from './logisticTasks.api';
import colorsOfRoutes from '../colorsOfRoutes';
import isValidCoordinates from '../../../../shared/utils/isValidCoordinates';
import isValidDate from '../../../../shared/utils/isValidDate';
import { parseLine } from '../../../../shared/geo-map/lib';

export const ROUTE_NAME_TPLS = [
  { value: '$task - $vehicle - $date', label: '<Название задачи> - <Номер ТС> - <Дата>' },
  { value: '$task - $vehicle_name - $date', label: '<Название задачи> - <Название ТС> - <Дата>' },
  { value: '$date - $task - $vehicle', label: '<Дата> - <Название задачи> - <Номер ТС>' },
  { value: '$date - $task - $vehicle_name', label: '<Дата> - <Название задачи> - <Название ТС>' },
  { value: '$task - $vehicle', label: '<Название задачи> - <Номер ТС>' },
  { value: '$task - $vehicle_name', label: '<Название задачи> - <Название ТС>' },
  { value: '$vehicle - $date', label: '<Номер ТС> - <Дата>' },
  { value: '$vehicle_name - $date', label: '<Название ТС> - <Дата>' },
  { value: '$date - $vehicle', label: '<Дата> - <Номер ТС>' },
  { value: '$date - $vehicle_name', label: '<Дата> - <Название ТС>' },
];

const parseLonLat = (lon, lat) => {
  if (!isValidCoordinates(lon, lat)) {
    return null;
  }
  return fromLonLat([lon, lat]);
};

const parseTime = (date) => {
  if (!isValidDate(date)) {
    return '';
  }
  return date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
};

// Формирует точки по заказу
const createPointsFromOrder = (order, route, color) => {
  const loadingPoint = {
    time: order.loadingTime ? new Date(order.loadingTime) : null,
    isLoading: true,
    isUnloading: false,
    orderNumber: order.requestId,
    label: 'Погрузка',
    coordinates: parseLonLat(order.loadingLon, order.loadingLat),
    addresses: {
      loadingAddress: order.loadingAddress,
      deliveryAddress: order.deliveryAddress,
    },
    color,
    r: route,
  };
  loadingPoint.tooltipText = `${loadingPoint.orderNumber}
  ${loadingPoint.label}
  ${parseTime(loadingPoint.time)}`;
  const unloadingPoint = {
    time: order.deliveryTime ? new Date(order.deliveryTime) : null,
    isLoading: false,
    isUnloading: true,
    label: 'Выгрузка',
    orderNumber: order.requestId,
    coordinates: parseLonLat(order.deliveryLon, order.deliveryLat),
    addresses: {
      loadingAddress: order.loadingAddress,
      deliveryAddress: order.deliveryAddress,
    },
    color,
    r: route,
  };
  unloadingPoint.tooltipText = `${unloadingPoint.orderNumber}
  ${unloadingPoint.label}
  ${parseTime(unloadingPoint.time)}`;
  return { loadingPoint, unloadingPoint };
};

// Преобразует источник и решение в связные структуры
const saturateSourceWithSolution = (source, solution) => {
  const newSourse = JSON.parse(JSON.stringify(source));
  let routePointsObject = {};
  const lines = [];
  const tracks = [];

  // К каждому ТС из решения находим ТС в источнике, добавляем данные из решения
  solution?.routes?.forEach((r, i) => {
    let routePoints = []; // Все точки для одного ТС (маршрута)
    const color = colorsOfRoutes[i % colorsOfRoutes.length]; // цвет маршрута

    const veh = newSourse?.availableTankers?.find(
      (vehDetail) => vehDetail.registrationNum === r.tanker.registrationNum,
    );
    veh.route = r;
    veh.color = color;
    veh.loadFactor = r.tanker.loadFactor;
    // length в route это пробег в км, а не длина массива
    veh.mileage = r.length;

    // Если есть координаты точки СТАРТА для ТС, добавляем точку старта
    const startCoords = parseLonLat(r.tanker.startLon, r.tanker.startLat);
    if (startCoords) {
      const startPoint = {
        id: crypto.randomUUID(),
        coordinates: startCoords,
        label: 'Старт',
        color,
        startIcon: true,
        r,
      };
      startPoint.tooltipText = `${startPoint.label}`;
      routePoints.push(startPoint);
    }

    // Для всех заказов в решении находим заказ в источнике, добавляем данные из решения
    r.orders?.forEach((solOrder) => {
      const order = newSourse?.orderSourceList?.find((ord) => ord.requestId === solOrder?.orderSource?.requestId);
      if (order) {
        order.vehicleNumber = r.tanker.registrationNum;
        order.statuses = solOrder.statuses;
        order.color = color;
        order.loadingAddress = solOrder.orderSource?.loadingAddress;
        order.loadingTime = solOrder.loadingTime;
        order.deliveryTime = solOrder.deliveryTime;
        order.loadingLat = solOrder.orderSource?.loadingLat;
        order.loadingLon = solOrder.orderSource?.loadingLon;
        order.deliveryLat = solOrder.orderSource?.deliveryLat;
        order.deliveryLon = solOrder.orderSource?.deliveryLon;
        order.isSolved = true;

        const points = createPointsFromOrder(order, r, color); // формируем точки
        routePoints.push(points.loadingPoint);
        routePoints.push(points.unloadingPoint);
      }
    });

    // Сортируем точки по времени из решения
    routePoints = routePoints.sort((a, b) => a.time - b.time);

    // Если есть координаты точки ФИНИША для ТС, добавляем точку финиша
    const finishCoords = parseLonLat(r.tanker.finishLon, r.tanker.finishLat);
    if (finishCoords) {
      const finishPoint = {
        id: crypto.randomUUID(),
        coordinates: finishCoords,
        label: 'Финиш',
        endIcon: true,
        color,
        r,
      };
      finishPoint.tooltipText = `${finishPoint.label}`;
      routePoints.push(finishPoint);
    }

    // Для соединения точек формируем из них линии по маршруту
    lines.push({
      r,
      color,
      id: crypto.randomUUID(),
      coordinates: routePoints.reduce((prev, routePoint) => {
        if (routePoint.coordinates) {
          prev.push(routePoint.coordinates);
        }
        return prev;
      }, []),
    });

    // Для соединения точек формируем из них треки по маршруту
    tracks.push({
      r,
      color,
      id: crypto.randomUUID(),
      coordinates: r.track ? parseLine(r.track) : null,
    });

    // Преобразуем массив точек в объкт для более удобного редактирования
    routePoints.forEach((routePoint, index) => {
      const id = crypto.randomUUID();
      routePointsObject = {
        ...routePointsObject,
        [`${id}`]: {
          id,
          ...routePoint,
          index: index + 1,
          time: routePoint.time?.toISOString(),
        },
      };
    });
  });

  // Для нераспределенных точек проставляем статусы
  solution?.notClosedRequests?.forEach((unallocatedOrder) => {
    const order = newSourse?.orderSourceList?.find(
      (orderSource) => orderSource.requestId === unallocatedOrder.requestId,
    );
    if (order) {
      order.statuses = unallocatedOrder.statuses;
    }
  });

  // Из оставшихся заказов, которых нет в решении, формируем точки
  newSourse?.orderSourceList.forEach((orderSource) => {
    if (!orderSource.isSolved) {
      const points = createPointsFromOrder(orderSource, null, colors.grey[600]);
      if (points.unloadingPoint?.coordinates) {
        const unloadingId = crypto.randomUUID();
        routePointsObject = {
          ...routePointsObject,
          [unloadingId]: {
            id: unloadingId,
            ...points.unloadingPoint,
            index: 'B',
          },
        };
      }
    }
  });

  newSourse.points = routePointsObject;
  newSourse.lines = lines;
  newSourse.tracks = tracks;
  return newSourse;
};

// Преобразовать данные для таблицы ТС
const transformVehicles = (vehList = []) => {
  const newVehList = vehList?.map((veh) => {
    const statuses = veh?.route?.orders?.flatMap((order) => order?.statuses || []) ?? [];
    return {
      ...veh,
      id: veh.registrationNum,
      color: veh?.color,
      startWork: veh?.route?.routePoints?.[0]?.departureTime,
      finishWork: veh?.route?.routePoints?.[(veh?.route?.routePoints?.length || 1) - 1]?.arrivalTime,
      countOrders: veh.route?.orders?.length || 0,
      maxWeightPercent: veh.maxWeightPercents,
      liftingCapacity: veh.liftingCapacity,
      km: veh.route?.routeLength || 0,
      statuses,
      func: '',
    };
  });
  return newVehList;
};

// Функция для извлечения времени в формате "часы:минуты:секунды"
function extractTime2(dateTimeString) {
  const dateTime = new Date(dateTimeString);
  const hours = String(dateTime.getHours()).padStart(2, '0');
  const minutes = String(dateTime.getMinutes()).padStart(2, '0');
  const seconds = String(dateTime.getSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

function getTimesById(orders, id) {
  return orders?.reduce(
    (acc, order) => {
      if (order.orderSource && order.requestId === id) {
        // Извлекаем время доставки
        if (order.deliveryTime) {
          acc.deliveryTime = extractTime2(order.deliveryTime);
        }
        // Извлекаем время загрузки
        if (order.loadingTime) {
          acc.loadingTime = extractTime2(order.loadingTime);
        }
      }
      return acc;
    },
    { deliveryTime: '', loadingTime: '' },
  );
}

function extractTime(isoString) {
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
}

// Преобразовать данные для таблицы Заказов
// eslint-disable-next-line default-param-last
const transformOrders = (ordList = [], routes) => {
  return ordList?.map((order) => {
    const { deliveryTime, loadingTime } = (!order.color && getTimesById(routes, order?.requestId)) || {
      deliveryTime: '',
      loadingTime: '',
    };

    return {
      ...order,
      id: order?.requestId,
      orderNumber: order?.requestId,
      color: order.color,
      loading: order.color ? (order?.loadingTime ? extractTime(order?.loadingTime) : '') : loadingTime,
      unloading: order.color ? (order?.deliveryTime ? extractTime(order?.deliveryTime) : '') : deliveryTime,
      addressLoading: order?.loadingAddress,
      addressUnloading: order?.deliveryAddress,
      weight: order?.weight,
      petrolType: order?.petrolProduct?.name,
      statuses: order?.statuses || [],
    };
  });
};

export const transformData = (data) => {
  const sourceWithSolution = saturateSourceWithSolution(data?.source, data?.solution);
  return {
    ...data,
    sourceWithSolution: {
      availableTankers: transformVehicles(sourceWithSolution?.availableTankers),
      orderSourceList: transformOrders(sourceWithSolution?.orderSourceList, data?.solution?.notClosedRequests),
      points: sourceWithSolution.points,
      lines: sourceWithSolution.lines,
      tracks: sourceWithSolution.tracks,
    },
  };
};

// Название полей которые в data после функции from dto
// Название полей которые будут в сторе в секции reducerPath.adaptersResource/api`
export const LogisticTasksEnum = {
  data: 'data',
  reorderedData: 'reorderedData',
  selectedOrders: 'selectedOrders',
  initialData: 'initialData',
  selectedVehicles: 'selectedVehicles',
  routes: 'routes',
  rePlanDate: 'rePlanDate',
  routeNameTemplate: 'routeNameTemplate',
  editedCoordinates: 'editedCoordinates',
};

export const SPEED_PROFILES = {
  CAR: 'CAR',
  BIKE: 'BIKE',
  MOTO: 'MOTO',
  PEDESTRIAN: 'PEDESTRIAN',
};

export const OPTIMIZATION_CRITERIA = {
  MINIMIZE_VEHICLES: 'MINIMIZE_VEHICLES',
  EQUAL_MILEAGES: 'EQUAL_MILEAGES',
  EQUAL_ORDERS: 'EQUAL_ORDERS',
};

export const CARGO_PLACEMENT_CRITERIA = {
  CAPACITY_IN_KG: 'CAPACITY_IN_KG',
  CAPACITY_IN_UNITS: 'CAPACITY_IN_UNITS',
};

// Инициализация стейта в сторе
export const logisticTasksPetrolInitialState = {
  [LogisticTasksEnum.data]: {},
  [LogisticTasksEnum.reorderedData]: {},
  [LogisticTasksEnum.initialData]: {},
  [LogisticTasksEnum.selectedOrders]: [],
  [LogisticTasksEnum.selectedVehicles]: [],
  [LogisticTasksEnum.routes]: [],
  [LogisticTasksEnum.rePlanDate]: null,
  [LogisticTasksEnum.routeNameTemplate]: ROUTE_NAME_TPLS[0].value,
  [LogisticTasksEnum.editedCoordinates]: {},
};
// Типизация стора
export const logisticTasksResourceType = {
  [LogisticTasksEnum.data]: PropTypes.any,
};
// Создание слайса тут пишем инициализированный стейт и редюсеры
export const logisticTasksPetrolSlice = createSlice({
  name: `${reducerPath.logisticTasksPetrolResource}/counter`,
  initialState: logisticTasksPetrolInitialState,
  reducers: {
    setData(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    setSelectedOrders(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    setSelectedVehicles(state, action) {
      state.selectedVehicles = action.payload;
    },
    setVehicleAssigned(state, action) {
      const newData = transformData(action.payload);
      state.data = newData;
    },
    setRePlanDate(state, action) {
      state.rePlanDate = action.payload;
    },
    setRouteNameTemplate(state, action) {
      state.routeNameTemplate = action.payload;
    },
    setTaskName(state, action) {
      state.data.name = action.payload;
    },
    setEditedAddresses(state, action) {
      const newData = JSON.parse(JSON.stringify(state.data));
      const order = newData.source.orderSourceList.find((o) => o.requestId === action.payload.order.requestId);
      let solutionRouteOrder;
      newData.solution.routes?.find((r) => {
        const res = r.orders.find((o) => o.requestId === action.payload.order.requestId);
        if (res) {
          solutionRouteOrder = res;
        }
        return res;
      });
      const solutionUnallocatedOrder = newData.solution.notClosedRequests?.find(
        (o) => o.requestId === action.payload.order.requestId,
      );
      const solutionOrder = solutionRouteOrder || solutionUnallocatedOrder;

      if (order) {
        if (action.payload.key === 'addressLoading') {
          order.loadingLat = action.payload.searchResult.lat || null;
          order.loadingLon = action.payload.searchResult.lon || null;
          order.loadingAddress =
            action.payload.searchResult.sourceAddress || action.payload.searchResult.formattedAddress || null;
          if (solutionOrder) {
            solutionOrder.orderSource = solutionOrder?.orderSource && {
              ...solutionOrder.orderSource,
              loadingLat: action.payload.searchResult.lat || null,
              loadingLon: action.payload.searchResult.lon || null,
              loadingAddress:
                action.payload.searchResult.sourceAddress || action.payload.searchResult.formattedAddress || null,
            };
          }
        }
        if (action.payload.key === 'addressUnloading') {
          order.deliveryLat = action.payload.searchResult.lat || null;
          order.deliveryLon = action.payload.searchResult.lon || null;
          order.deliveryAddress =
            action.payload.searchResult.sourceAddress || action.payload.searchResult.formattedAddress || null;
          if (solutionOrder) {
            solutionOrder.orderSource = solutionOrder?.orderSource && {
              ...solutionOrder.orderSource,
              deliveryLat: action.payload.searchResult.lat || null,
              deliveryLon: action.payload.searchResult.lon || null,
              deliveryAddress:
                action.payload.searchResult.sourceAddress || action.payload.searchResult.formattedAddress || null,
            };
          }
        }
        return {
          ...state,
          data: transformData(newData),
          editedCoordinates: {
            ...state.editedCoordinates,
            [action.payload.order.requestId]: {
              ...state.editedCoordinates[action.payload.order.requestId],
              [action.payload.key]: action.payload.searchResult,
            },
          },
        };
      }
      return state;
    },
    setTaskOptions(state, action) {
      const { vehicleType, optimizationCriteria, cargoPlacementCriteria } = action.payload;
      const src = JSON.parse(JSON.stringify(state.data.source)) || {};
      src.vehicleType = vehicleType;
      src.optimizationCriteria = optimizationCriteria;
      src.cargoPlacementCriteria = cargoPlacementCriteria;
      state.data.source = src;
    },
    removeVehicleFromTask(state, action) {
      const newData = JSON.parse(JSON.stringify(state.data));
      const { vehicleToDelete, deleteVehicleOrders } = action.payload;

      const solutionRouteIndex = newData.solution?.routes.findIndex(
        (r) => r.tanker.registrationNum === vehicleToDelete.registrationNum,
      );
      const route = newData.solution?.routes[solutionRouteIndex];
      route?.orders?.forEach((order) => {
        if (deleteVehicleOrders) {
          const srcOrderIndex = newData.source.orderSourceList.findIndex((o) => o.requestId === order.requestId);
          newData.source.orderSourceList.splice(srcOrderIndex, 1);
        } else {
          newData.solution?.notClosedRequests.push(order);
        }
      });
      newData.solution?.routes.splice(solutionRouteIndex, 1);

      const srcVehicleIndex = newData.source.availableTankers.findIndex(
        (v) => v.registrationNum === vehicleToDelete.registrationNum,
      );
      newData.source.availableTankers.splice(srcVehicleIndex, 1);

      const newSelectedVehicles = state.selectedVehicles.filter((v) => vehicleToDelete.registrationNum !== v);

      return {
        ...state,
        selectedVehicles: newSelectedVehicles,
        data: transformData(newData),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(logisticTasksApi.endpoints.getLogisticTaskId.matchFulfilled, (state, action) => {
      if (action.payload?.type === 'PETROL_DELIVERY') {
        const newData = transformData(action.payload);
        state.initialData = newData;
        state.data = newData;
      }
    });
  },
});

export const logisticTasksPetrolResourceActions = logisticTasksPetrolSlice.actions;
export const logisticTasksPetrolResourceReducer = logisticTasksPetrolSlice.reducer;
